import type { GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { WorkspaceCollectionsMap } from '#layers/workspace/types/collections';
import { CREATE_MODE_ITEM_ID, DisplayTemplateAstType, fieldIdToDisplayTemplateAst, findCollectionPrimitiveFieldByApiName, getFieldIds } from '#layers/data-model/public';
import { useFileCollection } from '#layers/workspace/composables/use-file-collection';

const MONOSPACE_FILES_THUMBNAIL_ID = '9b3ea5f2-e43b-44d0-83f3-e2d97dfff065' as GlobalSingleRelationFieldId;

export const WORKSPACE_COLLECTIONS: WorkspaceCollectionsMap = {
	MonospaceFile: {
		routes: {
			listing: {
				name: 'assets.library',
			},
			detail: (key) => {
				if (key === CREATE_MODE_ITEM_ID) {
					return {
						name: 'assets.library',
					};
				}

				return {
					name: 'assets.detail',
					params: {
						id: key?.id,
					},
				};
			},
		},
		schema: (collection) => {
			const filenameFieldId = findCollectionPrimitiveFieldByApiName(collection, 'fileName')?.id;
			return {
				id: {
					meta: {
						isHidden: true,
					},
				},
				// Editable content first (a0-a3), then read-only technical metadata (a4-a6).
				title: {
					meta: {
						sort: 'a1',
					},
				},
				description: {
					meta: {
						inputId: '$builtin:InputTextarea',
						sort: 'a2',
					},
				},
				tags: {
					meta: {
						inputId: '$builtin:InputTags',
						sort: 'a3',
					},
				},
				fileName: {
					meta: {
						inputOptions: {
							disabled: true,
						},
						sort: 'a4',
					},
				},
				mediaType: {
					meta: {
						displayId: '$builtin:DisplayFormattedValue',
						displayOptions: {
							fontFamily: 'mono',
						},
						inputOptions: {
							disabled: true,
						},
						sort: 'a5',
					},
				},
				fileSize: {
					meta: {
						inputId: '$builtin:DisplayFileSize',
						displayId: '$builtin:DisplayFileSize',
						sort: 'a6',
					},
				},
				// Engine-managed timestamps: surfaced through sorting and filtering, not the form.
				createdAt: {
					meta: {
						isHidden: true,
						displayId: '$builtin:DisplayFormattedDateTime',
						displayOptions: {
							displayMode: 'relative',
						},
					},
				},
				updatedAt: {
					meta: {
						isHidden: true,
						displayId: '$builtin:DisplayFormattedDateTime',
						displayOptions: {
							displayMode: 'relative',
						},
					},
				},
				$meta: {
					displayName: '$t:common.assets',
					displayTemplate: {
						type: DisplayTemplateAstType.Doc,
						content: [
							{
								type: DisplayTemplateAstType.Ref,
								attrs: {
									fieldId: MONOSPACE_FILES_THUMBNAIL_ID,
								},
							},

							...(filenameFieldId
								? [
										{
											type: DisplayTemplateAstType.Text,
											// Acts as separator between the thumbnail and field id
											// TODO: Solve this properly, e.g. a separator node
											text: '  ',
										},
										{
											type: DisplayTemplateAstType.Ref,
											attrs: {
												fieldId: filenameFieldId,
											},
										},
									] as const
								: []),
						],
					},
				},
				$extend({ addSingleRelationField }) {
					addSingleRelationField('$thumbnail', 'MonospaceFile', {
						id: MONOSPACE_FILES_THUMBNAIL_ID,
						fields: {
							'MonospaceFile.id': 'MonospaceFile.id',
						},
						meta: {
							displayName: '$t:common.preview',
							inputId: '$builtin:InputFileM2O',
							inputOptions: {
								showRemove: false,
							},
							sort: 'a0',
						},
					});
				},
			};
		},
		listing: {
			defaultView: () => {
				const { titleField, thumbnailField, mediaTypeField, createdAtField } = useFileCollection();

				return {
					layoutId: '$builtin:LayoutCards',
					displayOptions: {
						titleTemplate: titleField.value ? fieldIdToDisplayTemplateAst(titleField.value.id) : null,
						descriptionTemplate: mediaTypeField.value ? fieldIdToDisplayTemplateAst(mediaTypeField.value.id) : null,
						thumbnailField: thumbnailField.value?.id,
						// Omitted rather than left undefined so the layout keeps its primary-key fallback.
						...(createdAtField.value && {
							sorting: [{ fieldId: createdAtField.value.id, direction: 'desc' as const }],
						}),
					},
				};
			},
		},
	},
	MonospacePolicy: {
		routes: {
			listing: {
				name: 'workspace.settings.access.policies.listing',
			},
			detail: (key) => ({
				name: 'workspace.settings.access.policies.detail',
				params: {
					policy: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnIds: getFieldIds(collection, ['name']),
				},
			}),
		},
		schema: {
			$meta: {
				displayName: '$t:common.policies',
				displayTemplate: '{{ name }}',
			},
			id: {
				meta: {
					isHidden: true,
				},
			},
			name: {
				meta: {
					sort: 'a0',
				},
			},
			content: {
				meta: {
					displayName: '',
					inputId: '$builtin:InputPolicyDocument',
					sort: 'a1',
				},
			},
			filterFields: {
				meta: {
					isHidden: true,
				},
			},
			roles: {
				meta: {
					isHidden: true,
				},
			},
			isProtected: {
				meta: {
					isHidden: true,
				},
			},
		},
	},
	MonospaceRole: {
		routes: {
			listing: {
				name: 'workspace.settings.access.roles.listing',
			},
			detail: (key) => ({
				name: 'workspace.settings.access.roles.detail',
				params: {
					id: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		schema: {
			$meta: {
				displayName: '$t:common.roles',
				displayTemplate: '{{ isPublic }} {{ name }}',
			},
			id: {
				meta: {
					isHidden: true,
				},
			},
			parent: {
				meta: {
					// TODO make field labels translatable
					// displayName: '$t:collections.monospace_role.parent.display_name',
					inputOptions: {
						showCreate: false,
						showEdit: false,
					},
				},
			},
			policies: {
				meta: {
					inputId: '$builtin:InputPolicyAssignment',
				},
			},
			directChildren: {
				meta: {
					isHidden: true,
				},
			},
			descendants: {
				meta: {
					isHidden: true,
				},
			},
			ancestors: {
				meta: {
					isHidden: true,
				},
			},
			users: {
				meta: {
					isHidden: true,
				},
			},
			isPublic: {
				meta: {
					isHidden: true,
					displayId: '$builtin:DisplayLabels',
					displayName: '$t:pages.settings.access.pages.roles.purpose',
					displayOptions: {
						displayStyle: 'dot-with-label',
						convertToTitleCase: false,
						options: [
							{
								value: true,
								label: '$t:pages.settings.access.pages.roles.public_badge.public',
								icon: 'lucide:globe',
								color: 'var(--ui-success)',
							},
							{
								value: false,
								label: '$t:pages.settings.access.pages.roles.public_badge.non_public',
								icon: 'lucide:users',
								color: 'var(--ui-warning)',
							},
						],
					},
				},
			},
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnIds: getFieldIds(collection, ['name', 'parent', 'policies', 'isPublic']),
					sorting: [
						{ fieldId: findCollectionPrimitiveFieldByApiName(collection, 'isPublic')?.id, direction: 'desc' },
					],
				},
			}),
		},
	},
	MonospaceSystemUser: {
		routes: {
			listing: {
				name: 'workspace.settings.access.members.listing',
			},
			detail: (key) => ({
				name: 'workspace.settings.access.members.detail',
				params: {
					id: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnIds: getFieldIds(collection, ['fullName', 'email', 'roles', 'lastActivityAt']),
				},
			}),
		},
		schema: {
			$meta: {
				displayName: '$t:common.members',
				// TODO update to use profile picture
				displayTemplate: '{{ fullName }}',
			},
			id: {
				meta: {
					isHidden: true,
					typeTemplateId: 'identifier',
				},
			},
			email: {
				meta: {
					inputOptions: {
						disabled: true,
					},
				},
			},
			fullName: {
				meta: {
					// TODO rework display names such that they can be translated from placeholders
					// displayName: '$t:common.name',
				},
			},
			roles: {
				meta: {
					inputId: '$builtin:InputRoleAssignment',
				},
			},
			avatarId: {
				meta: {
					isHidden: true,
				},
			},
			storageValues: {
				meta: {
					isHidden: true,
				},
			},
			isService: {
				meta: {
					isHidden: true,
				},
			},
			serviceOwnerId: {
				meta: {
					isHidden: true,
				},
			},
			appearance: {
				meta: {
					isHidden: true,
				},
			},
			lastActivityAt: {
				meta: {
					displayName: '$t:pages.settings.access.pages.members.table.last_activity',
					isHidden: true,
					displayId: '$builtin:DisplayFormattedDateTime',
					displayOptions: {
						displayMode: 'relative',
					},
				},
			},
		},
	},
	MonospaceSpace: {
		schema: {
			$meta: {
				displayTemplate: '{{ title }}',
			},
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnIds: getFieldIds(collection, ['title', 'description']),
				},
			}),
		},
	},
	MonospaceUserRole: {
		schema: {
			$meta: {
				displayTemplate: '{{ role }}',
			},
		},
	},
	MonospaceRolePolicy: {
		schema: {
			$meta: {
				displayTemplate: '{{ policy }}',
			},
		},
	},
} as const;
